import type {
  FieldOption,
  GrantSubCategoryEntry,
} from '../constants/field-options-registry.constant';
import type { GrantTemplateContext } from '../types/template-field-context.types';

const appendUnique = (target: FieldOption[], source: FieldOption[]) => {
  const existing = new Set(target.map((f) => f.value));
  for (const item of source) {
    if (!existing.has(item.value)) {
      target.push(item);
      existing.add(item.value);
    }
  }
};

const expandIndexedFields = (
  fields: FieldOption[],
  milestonesCount: number,
  periodsCount: number,
): FieldOption[] => {
  const result: FieldOption[] = [];

  for (const field of fields) {
    const hasN = field.value.includes('-n-');
    if (!hasN) {
      result.push(field);
      continue;
    }

    const isMilestone = field.value.startsWith('milestone-n-');
    const count = isMilestone ? milestonesCount : periodsCount;

    if (count === 0) continue;

    for (let i = 1; i <= count; i++) {
      result.push({
        value: field.value.replace(/-n-/g, `-${i}-`),
        labelKey: field.labelKey,
        labelKeyParams: { n: i },
      });
    }
  }

  return result;
};

export const resolveGrantFields = (
  entry: GrantSubCategoryEntry,
  context: GrantTemplateContext,
): FieldOption[] => {
  const {
    hasAccelerationDate,
    hasCliff,
    hasVesting,
    planType,
    vestingType,
    milestonesCount = 0,
    periodsCount = 0,
  } = context;

  const merged: FieldOption[] = [...entry.base];

  if (planType && entry.byPlanType) {
    const planEntry = entry.byPlanType[planType];
    if (planEntry) {
      appendUnique(merged, planEntry.base);

      if (vestingType && planEntry.byVestingType) {
        const vestingFields = planEntry.byVestingType[vestingType];
        if (vestingFields) {
          appendUnique(merged, vestingFields);
        }
      }
    }
  }

  const expanded = expandIndexedFields(merged, milestonesCount, periodsCount);

  return expanded.filter((field) => {
    if (field.value === 'acceleration-date') return hasAccelerationDate !== false;
    if (field.value === 'cliff') return hasCliff !== false;
    if (field.value === 'vesting-type') return hasVesting === true;
    return true;
  });
};
